import type { AdminRole } from '../api/types';

/**
 * Capability model for the CMS. This mirrors the backend's authoritative gates:
 * SUPER_ADMIN + ADMIN (the `ELEVATED` set in admin.controller.ts) may perform any
 * state-changing action; FINANCE additionally holds the money duties in the
 * backend's `FINANCE_OPS` set; SUPPORT is read-only outside compliance review.
 *
 * The UI uses this to HIDE/DISABLE controls a role cannot use, but the server
 * @Roles guard remains the source of truth — never trust the client alone.
 *
 * NOTE: FINANCE *can* act on finance routes — the backend's FINANCE_OPS gate
 * (SUPER_ADMIN + ADMIN + FINANCE) covers withdrawal review, recharge confirm and
 * balance adjust, and FINANCE_READ / REPORTS_EXPORT cover the ledger, order
 * queues, order detail, reconciliation and the full-ledger export. `finance.write`
 * below mirrors FINANCE_OPS exactly; it is not an aspirational placeholder.
 */
export type Capability =
  | 'users.write'        // adjust balance, ban
  | 'finance.write'      // approve/reject withdrawals, confirm recharges
  | 'games.write'        // force-draw, odds, config
  | 'content.write'      // banners, notices, settings, CS
  | 'payments.write'     // toggle / manage payment methods
  | 'engagement.write'   // activities, vip, point mall
  | 'affiliate.write'    // commission config
  | 'admins.manage'      // manage admin accounts + roles
  | 'settings.write'     // platform settings, feature flags
  | 'compliance.write'   // KYC review, responsible-gambling limits/exclusions
  | 'risk.write'         // triage/resolve risk & AML alerts
  | 'audit.read'
  | 'reports.export'
  | 'finance.read'      // view the recharge/withdrawal order queues + detail
  | 'ledger.read';      // view the full ledger and reconciliation exceptions

const ELEVATED: AdminRole[] = ['SUPER_ADMIN', 'ADMIN'];
// FINANCE can perform its core money duties (withdrawal review, recharge
// confirm, balance adjust) — mirrors the backend FINANCE_OPS gate.
const FINANCE_OPS: AdminRole[] = ['SUPER_ADMIN', 'ADMIN', 'FINANCE'];

const MATRIX: Record<Capability, AdminRole[]> = {
  'users.write': ELEVATED,
  'finance.write': FINANCE_OPS,
  'games.write': ELEVATED,
  'content.write': ELEVATED,
  'payments.write': ELEVATED,
  'engagement.write': ELEVATED,
  'affiliate.write': ELEVATED,
  'admins.manage': ['SUPER_ADMIN'],
  'settings.write': ELEVATED,
  'compliance.write': ['SUPER_ADMIN', 'ADMIN', 'SUPPORT'],
  'risk.write': ELEVATED,
  'audit.read': ['SUPER_ADMIN', 'ADMIN', 'SUPPORT', 'FINANCE'],
  'reports.export': ['SUPER_ADMIN', 'ADMIN', 'FINANCE'],
  // Order queues include SUPPORT: answering "where is my deposit?" is a support
  // duty, and these payloads are order-scoped and PII-masked. Mirrors the
  // backend ORDER_QUEUE_READ gate.
  'finance.read': ['SUPER_ADMIN', 'ADMIN', 'FINANCE', 'SUPPORT'],
  // The full ledger and reconciliation are unbounded money history — SUPPORT is
  // excluded. Mirrors the backend LEDGER_READ gate.
  'ledger.read': FINANCE_OPS,
};

export function can(role: AdminRole | null | undefined, capability: Capability): boolean {
  if (!role) return false;
  return MATRIX[capability]?.includes(role) ?? false;
}

export const ROLE_LABELS: Record<AdminRole, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  SUPPORT: 'Support',
  FINANCE: 'Finance',
};

/** Tailwind classes for the role badge. */
export const ROLE_BADGE: Record<AdminRole, string> = {
  SUPER_ADMIN: 'bg-primary/15 text-primary',
  ADMIN: 'bg-info/15 text-info',
  SUPPORT: 'bg-muted text-muted-foreground',
  FINANCE: 'bg-success/15 text-success',
};
